import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckCircleIcon, MousePointerClickIcon, SearchIcon, LayersIcon, PuzzleIcon, SmartphoneIcon, ZapIcon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon, WrenchIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WordPressWebsitesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'WordPress websites | Flexibel & SEO-vriendelijk | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'WordPress website laten maken? Flexibele, snelle en SEO-vriendelijke websites gebouwd in WordPress. Zelf beheerbaar, veilig en uitbreidbaar. Vraag een offerte aan.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'WordPress websites | Flexibel & SEO-vriendelijk | Noord',
      'og:description': 'WordPress website laten maken? Flexibele, snelle en SEO-vriendelijke websites gebouwd in WordPress.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/wordpress-websites',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Is WordPress veilig voor mijn bedrijf?',
    a: 'Ja, WordPress is zeer veilig mits het goed wordt onderhouden. Wij zorgen voor de juiste beveiligingsplugins, sterke wachtwoorden en regelmatige updates om jouw wordpress website optimaal te beveiligen tegen hackers.'
  }, {
    q: 'Kan ik zelf content aanpassen?',
    a: "Absoluut! Dat is de grootste kracht van een WordPress website laten maken. Het systeem is erg gebruiksvriendelijk. Na oplevering krijg je van ons uitleg zodat je zelf eenvoudig teksten, afbeeldingen en nieuwe pagina's kunt toevoegen."
  }, {
    q: 'Hoe snel is een WordPress website?',
    a: "Een wordpress website kan razendsnel zijn, mits goed gebouwd. Wij gebruiken lichte thema's, optimaliseren afbeeldingen en gebruiken snelle hosting en caching om ervoor te zorgen dat jouw site direct laadt."
  }, {
    q: 'Is WordPress goed voor SEO?',
    a: 'WordPress is van nature zeer SEO-vriendelijk. Met de juiste inrichting en plugins (zoals Yoast of RankMath) heb je volledige controle over je vindbaarheid. Elk wordpress webdesign dat wij opleveren is technisch geoptimaliseerd voor Google.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-3341">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-3342">
        <div className="absolute inset-0 pointer-events-none" data-id="element-3343">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-3344" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-3345" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-3346" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3347">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-3348">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-3349">
              Home
            </a>
            <span className="text-navy-500" data-id="element-3350">/</span>
            <span className="text-accent-light font-medium" data-id="element-3351">
              WordPress websites
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-3352">
            WordPress websites die groeien met je bedrijf
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-3353">
            Flexibele, snelle en SEO-vriendelijke websites gebouwd in WordPress.
            De perfecte basis voor jouw online succes.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-3354">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-3355">
              Plan een gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-3356" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom WordPress Section */}
      <section className="py-24 bg-surface-muted" data-id="element-3357">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3358">
          <div className="text-center mb-16" data-id="element-3359">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3360">
              Waarom een WordPress website laten maken?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3361">
              Meer dan 40% van alle websites wereldwijd draait op WordPress. En
              dat is niet voor niets.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3362">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3363">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3364">
                <MousePointerClickIcon className="w-6 h-6 text-accent" data-id="element-3365" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3366">
                Gebruiksvriendelijk
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3367">
                Beheer zelf eenvoudig je teksten, afbeeldingen en blogartikelen
                zonder technische kennis.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3368">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3369">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-3370" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3371">
                SEO vriendelijk
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3372">
                De code structuur van WordPress wordt perfect begrepen door
                Google voor betere vindbaarheid.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3373">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3374">
                <LayersIcon className="w-6 h-6 text-accent" data-id="element-3375" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3376">
                Flexibel uitbreidbaar
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3377">
                Begin klein en breid later eenvoudig uit met een webshop,
                reserveringssysteem of leeromgeving.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3378">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3379">
                <PuzzleIcon className="w-6 h-6 text-accent" data-id="element-3380" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3381">
                Veel integraties
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3382">
                Koppel moeiteloos met je favoriete CRM, nieuwsbrief software of
                boekhoudpakket.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat je krijgt */}
      <section className="py-24 bg-white" data-id="element-3383">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3384">
          <div className="text-center mb-16" data-id="element-3385">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3386">
              Standaard inbegrepen bij elk project
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3387">
              Wanneer je kiest voor ons wordpress webdesign, krijg je altijd een
              compleet product.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-3388">
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3389">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3390">
                <SmartphoneIcon className="w-6 h-6 text-navy-700" data-id="element-3391" />
              </div>
              <div data-id="element-3392">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3393">
                  Responsive design
                </h3>
                <p className="text-slate-600" data-id="element-3394">
                  Jouw website werkt en oogt perfect op mobiel, tablet en
                  desktop. Cruciaal voor zowel gebruikerservaring als SEO.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3395">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3396">
                <SearchIcon className="w-6 h-6 text-navy-700" data-id="element-3397" />
              </div>
              <div data-id="element-3398">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3399">
                  SEO basis inrichting
                </h3>
                <p className="text-slate-600" data-id="element-3400">
                  We richten de technische SEO direct goed in en installeren
                  tools waarmee je zelf je vindbaarheid kunt optimaliseren.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3401">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3402">
                <ZapIcon className="w-6 h-6 text-navy-700" data-id="element-3403" />
              </div>
              <div data-id="element-3404">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3405">
                  Snelle laadtijd
                </h3>
                <p className="text-slate-600" data-id="element-3406">
                  Niemand houdt van wachten. We bouwen lichte websites en
                  gebruiken caching voor razendsnelle laadtijden.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3407">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3408">
                <ShieldCheckIcon className="w-6 h-6 text-navy-700" data-id="element-3409" />
              </div>
              <div data-id="element-3410">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3411">
                  Beveiliging
                </h3>
                <p className="text-slate-600" data-id="element-3412">
                  Standaard voorzien van SSL-certificaat, anti-spam maatregelen
                  en beveiligingsplugins om hackers buiten de deur te houden.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WordPress vs Andere systemen */}
      <section className="py-24 bg-navy-50" data-id="element-3413">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3414">
          <div className="text-center mb-16" data-id="element-3415">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3416">
              WordPress vergeleken
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3417">
              Waarom een wordpress website bedrijf vaak de beste keuze is.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-3418">
            <div className="bg-white p-8 rounded-card shadow-card border-2 border-accent relative" data-id="element-3419">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-white text-xs font-bold px-4 py-1 rounded-full" data-id="element-3420">
                Onze keuze
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-4 text-center" data-id="element-3421">
                WordPress
              </h3>
              <ul className="space-y-4" data-id="element-3422">
                <li className="flex items-start gap-3" data-id="element-3423">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3424" />
                  <span className="text-slate-700" data-id="element-3425">
                    100% eigendom van je website
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3426">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3427" />
                  <span className="text-slate-700" data-id="element-3428">Eindeloos uitbreidbaar</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3429">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3430" />
                  <span className="text-slate-700" data-id="element-3431">
                    Beste voor SEO resultaten
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3432">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3433" />
                  <span className="text-slate-700" data-id="element-3434">
                    Geen maandelijkse platformkosten
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-3435">
              <h3 className="text-2xl font-bold text-navy-900 mb-4 text-center" data-id="element-3436">
                Wix
              </h3>
              <ul className="space-y-4" data-id="element-3437">
                <li className="flex items-start gap-3" data-id="element-3438">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3439" />
                  <span className="text-slate-700" data-id="element-3440">
                    Makkelijk voor beginners
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3441">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3442">
                    ✕
                  </span>
                  <span data-id="element-3443">Je huurt de website (geen eigendom)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3444">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3445">
                    ✕
                  </span>
                  <span data-id="element-3446">Beperkte SEO mogelijkheden</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3447">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3448">
                    ✕
                  </span>
                  <span data-id="element-3449">Moeilijk te verhuizen</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-3450">
              <h3 className="text-2xl font-bold text-navy-900 mb-4 text-center" data-id="element-3451">
                Squarespace
              </h3>
              <ul className="space-y-4" data-id="element-3452">
                <li className="flex items-start gap-3" data-id="element-3453">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3454" />
                  <span className="text-slate-700" data-id="element-3455">
                    Mooie standaard templates
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3456">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3457">
                    ✕
                  </span>
                  <span data-id="element-3458">Minder flexibel dan WordPress</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3459">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3460">
                    ✕
                  </span>
                  <span data-id="element-3461">Hoge maandelijkse kosten</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3462">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3463">
                    ✕
                  </span>
                  <span data-id="element-3464">Beperkte integraties NL markt</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center" data-id="element-3465">
            <a href="#/kennisbank" className="inline-flex items-center gap-2 text-accent font-semibold hover:text-accent-hover transition-colors" data-id="element-3466">
              Lees ons uitgebreide artikel: WordPress vs Wix
              <ArrowRightIcon className="w-4 h-4" data-id="element-3467" />
            </a>
          </div>
        </div>
      </section>

      {/* WordPress Onderhoud */}
      <section className="py-24 bg-white" data-id="element-3468">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3469">
          <div className="bg-navy-900 rounded-3xl p-8 lg:p-12 text-center relative overflow-hidden" data-id="element-3470">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '24px 24px'
          }} data-id="element-3471" />
            <WrenchIcon className="w-12 h-12 text-accent mx-auto mb-6 relative z-10" data-id="element-3472" />
            <h2 className="text-3xl font-bold text-white mb-4 relative z-10" data-id="element-3473">
              Zorgeloos WordPress Onderhoud
            </h2>
            <p className="text-navy-200 text-lg mb-8 max-w-2xl mx-auto relative z-10" data-id="element-3474">
              Een WordPress website heeft regelmatig updates nodig om veilig en
              snel te blijven. Wij nemen dit technische beheer graag voor je uit
              handen.
            </p>
            <a href="#/hosting-onderhoud" className="inline-flex items-center gap-2 bg-white text-navy-900 hover:bg-navy-50 font-bold px-8 py-4 rounded-button shadow-soft transition-all duration-200 relative z-10" data-id="element-3475">
              Bekijk onze onderhoudspakketten
              <ArrowRightIcon className="w-5 h-5" data-id="element-3476" />
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-3477">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3478">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-3479">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-3480">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3481">
              Professionele website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3482">•</span>
            <a href="#/wordpress-vs-wix" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3483">
              WordPress of Wix kiezen?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3484">•</span>
            <a href="#/webdesign-alkmaar" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3485">
              Webdesign bureau Alkmaar
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3486">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3487">
              Website onderhoud uitbesteden
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-3488">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3489">
          <div className="text-center mb-16" data-id="element-3490">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3491">
              Veelgestelde vragen over WordPress
            </h2>
          </div>

          <div className="space-y-4" data-id="element-3492">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-3493">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-3494">
                  <span className="font-bold text-navy-900" data-id="element-3495">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-3496" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-3497" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-3498">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-3499" />
    </main>;
}